import React, { forwardRef, useEffect, useRef } from "react";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import Button from "./Button";

/**
 * IconButton Molecule - Circular button with icon only
 * Used for actions like voice search, share, close, etc.
 *
 * @component
 * @example
 * <IconButton icon={<MicrophoneIcon />} variant="primary" onClick={handleClick} />
 */
const IconButton = forwardRef(
  (
    {
      icon,
      variant = "primary",
      size = "md",
      className = "",
      disabled = false,
      withMotion = true,
      tooltip = null,
      tooltipPosition = "top",
      tooltipTheme = "green-tooltip",
      ...props
    },
    ref
  ) => {
    const internalRef = useRef(null);
    // Use external ref if provided, otherwise use internal ref
    const buttonRef = ref || internalRef;

    useEffect(() => {
      if (tooltip && buttonRef.current) {
        const instance = tippy(buttonRef.current, {
          content: tooltip,
          placement: tooltipPosition,
          arrow: true,
          theme: tooltipTheme,
        });

        return () => {
          instance.destroy();
        };
      }
    }, [tooltip, tooltipPosition, tooltipTheme, buttonRef]);

    // Map IconButton specific variants if needed, or rely on Button variants
    // IconButton "outlineClicked" is specific, we can handle it via className or map it
    const getVariantClasses = () => {
      if (variant === "outlineClicked") {
        return "text-red-500 hover:bg-red-400 hover:text-grey-500 [&_svg]:hover:text-grey-500 border-none";
      }
      if (variant === "actionButton") {
        return "rounded-full bg-grey-200 hover:bg-grey-300 text-grey-800 p-2.5";
      }
      if (variant === "actionButtonActive") {
        return "rounded-full bg-green-600 text-grey-100 hover:bg-green-700 hover:text-grey-500 p-2.5";
      }
      if (variant === "actionButtonDelete") {
        return "rounded-full bg-grey-200 hover:bg-red-600 text-red-400 hover:text-grey-500 p-2.5";
      }
      return "";
    };

    const buttonElement = (
      <Button
        ref={buttonRef}
        variant={
          variant === "outlineClicked" ||
            variant === "actionButton" ||
            variant === "actionButtonActive" ||
            variant === "actionButtonDelete"
            ? "ghost"
            : variant
        }
        size={size}
        rounded="full"
        className={`${getVariantClasses()} ${className}`}
        disabled={disabled}
        withMotion={withMotion}
        {...props}
      >
        {icon}
      </Button>
    );

    if (tooltip) {
      return (
        <>
          {buttonElement}
        </>
      );
    }

    return buttonElement;
  }
);

IconButton.displayName = "IconButton";

export default IconButton;
